import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ClipboardList, Pencil, Plus } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { AlertBanner, ContentCard, DataTable, EmptyState, LoadingState, PageHeader, SearchInput, ui } from "../../../components/ui";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { TablePagination } from "../../../components/ui/TablePagination";
import { cn } from "../../../lib/utils";
import { LeaveActionToast } from "../../leave-management/components/LeaveActionToast";
import { FormDialog, STATUS_TABS, StatusTabs, StatusToggle, fieldLabelClass, iconBtnClass, inputClass } from "./ProductServicesPage";
import {
  addProductComplaintType,
  getProductComplaintType,
  getProductService,
  listProductComplaintTypes,
  updateProductComplaintType,
  updateProductComplaintTypeStatus,
} from "../../../services/fsmService";

const K = "fsmComplaintTypes.";
const PAGE_SIZE = 10;
const TYPE_MAX = 150;
const PRODUCTS_PATH = "/fsm/products-services";

/** `record` is the detail response when editing, null when adding. */
function ComplaintTypeDialog({ open, productServiceId, record, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = Boolean(record);
  const [complaintType, setComplaintType] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState("");
  const [banner, setBanner] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setComplaintType(record?.complaintType ?? "");
    setDescription(record?.complaintDescription ?? "");
    setIsActive(true);
    setError("");
    setBanner("");
  }, [open, record]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    const trimmed = complaintType.trim();
    const nextError = !trimmed
      ? t("validation.fieldRequired", { field: t(`${K}complaintType`) })
      : trimmed.length > TYPE_MAX ? t(`${K}typeTooLong`, { max: TYPE_MAX }) : "";
    setError(nextError);
    if (nextError) return;

    const payload = {
      productServiceId: Number(productServiceId),
      complaintType: trimmed,
      complaintDescription: description.trim(),
      isActive: isEdit ? record.isActive : isActive,
    };
    setSubmitting(true);
    setBanner("");
    try {
      if (isEdit) await updateProductComplaintType(record.complaintTypeId, payload);
      else await addProductComplaintType(payload);
      onSaved(isEdit);
    } catch (err) {
      setBanner(err?.message || t(`${K}saveFailed`));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <FormDialog open={open} title={t(isEdit ? `${K}editItem` : `${K}newItem`)} banner={banner} submitting={submitting}
      submitLabel={t(isEdit ? "formActions.save" : "formActions.create")} onClose={onClose} onSubmit={handleSubmit}>
      <div>
        <label className={fieldLabelClass}>{t(`${K}complaintType`)} <span className="text-error">*</span></label>
        <input value={complaintType} onChange={(e) => { setComplaintType(e.target.value); setError(""); }}
          className={cn(inputClass, error ? "border-error" : "border-(--color-text-border)")} />
        {error ? <p className="text-[11px] text-error mt-1">{error}</p> : null}
      </div>

      <div>
        <label className={fieldLabelClass}>
          {t("common.description2")} <span className="font-normal text-muted-foreground">{t("common.optional")}</span>
        </label>
        <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)}
          className="rounded-xl bg-white text-sm border-(--color-text-border)" />
      </div>

      {!isEdit ? (
        <div>
          <label className={fieldLabelClass}>{t("status.status")}</label>
          <StatusToggle active={isActive} name={complaintType} disabled={submitting} onToggle={() => setIsActive((current) => !current)} />
        </div>
      ) : null}
    </FormDialog>
  );
}

export function ProductComplaintTypesPage() {
  const { t } = useTranslation();
  const { productServiceId } = useParams();
  const [productName, setProductName] = useState("");
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [type, setType] = useState("");
  const [statusTab, setStatusTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [dialog, setDialog] = useState({ open: false, record: null });
  const [busyId, setBusyId] = useState(null);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    getProductService(productServiceId)
      .then((product) => setProductName(product?.productServiceName ?? ""))
      .catch(() => setProductName(""));
  }, [productServiceId]);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const { isActive } = STATUS_TABS.find((tab) => tab.key === statusTab);
      const result = await listProductComplaintTypes({ productServiceId, type, isActive, page: page - 1, size: PAGE_SIZE });
      if (request !== requestRef.current) return;
      setRows(result.content);
      setTotalItems(result.totalElements);
      setTotalPages(Math.max(1, result.totalPages));
    } catch (err) {
      if (request !== requestRef.current) return;
      setRows([]);
      setError(err?.message || t(`${K}loadFailed`));
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [productServiceId, type, statusTab, page, t]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => load();

  // The API filters by type name or by status, not both, so picking one clears the other.
  const selectTab = (key) => { setStatusTab(key); setType(""); setPage(1); };
  const searchType = (value) => { setType(value); setStatusTab("all"); setPage(1); };

  async function openEdit(id) {
    setBusyId(id);
    try {
      setDialog({ open: true, record: await getProductComplaintType(id) });
    } catch (err) {
      setToast({ type: "error", message: err?.message || t(`${K}detailFailed`) });
    } finally {
      setBusyId(null);
    }
  }

  async function handleToggle(row) {
    setBusyId(row.complaintTypeId);
    try {
      await updateProductComplaintTypeStatus(row.complaintTypeId, !row.isActive);
      setToast({ type: "success", message: t("fsmProductServices.statusUpdated") });
      refresh();
    } catch (err) {
      setToast({ type: "error", message: err?.message || t("fsmProductServices.statusFailed") });
    } finally {
      setBusyId(null);
    }
  }

  function handleSaved(isEdit) {
    setDialog({ open: false, record: null });
    setToast({ type: "success", message: t(isEdit ? `${K}updated` : `${K}created`) });
    refresh();
  }

  const editButton = (row) => (
    <Button variant="outline" className={iconBtnClass} disabled={busyId === row.complaintTypeId}
      onClick={() => openEdit(row.complaintTypeId)} aria-label={t("common.edit")}>
      <Pencil className="h-3.5 w-3.5 text-primary" />
    </Button>
  );

  const statusToggle = (row) => (
    <StatusToggle active={Boolean(row.isActive)} name={row.complaintType}
      disabled={busyId === row.complaintTypeId} onToggle={() => handleToggle(row)} />
  );

  return (
    <AppLayout overlay={<LeaveActionToast toast={toast} />}>
      <PageHeader back={<PageBackLink to={PRODUCTS_PATH} />}
        title={productName ? t(`${K}pageTitleFor`, { product: productName }) : t(`${K}pageTitle`)}
        action={
          <Button onClick={() => setDialog({ open: true, record: null })} className={ui.primaryBtn}>
            <Plus className="h-4 w-4" />
            {t(`${K}newItem`)}
          </Button>
        } />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <StatusTabs value={statusTab} onChange={selectTab} />
        <SearchInput submitOnEnter value={type} placeholder={t(`${K}searchByType`)}
          onChange={(e) => searchType(e.target.value.trim())} />
      </div>

      {error ? <AlertBanner type="error">{error}</AlertBanner> : null}

      <ContentCard>
        {loading ? (
          <LoadingState message={t("common.loading")} />
        ) : rows.length === 0 ? (
          <EmptyState icon={ClipboardList} title={t(`${K}noItems`)}
            message={t(type || statusTab !== "all" ? `${K}noMatch` : `${K}getStarted`)} />
        ) : (
          <>
            <DataTable minWidth="760px" columns={[
              { key: "type", label: t(`${K}complaintType`) },
              { key: "description", label: t("common.description2") },
              { key: "status", label: t("status.status") },
              { key: "actions", label: t("common.actions"), className: "text-right" },
            ]}>
              {rows.map((row) => (
                <tr key={row.complaintTypeId} className="hover:bg-workspace/20 transition-colors">
                  <td className="px-4 py-3.5">
                    <p className="font-semibold text-foreground text-sm truncate max-w-[260px]">{row.complaintType || "—"}</p>
                  </td>
                  <td className="px-4 py-3.5">
                    <p className="text-sm per-file-hint truncate max-w-[320px]">{row.complaintDescription || "—"}</p>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">{statusToggle(row)}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1.5">{editButton(row)}</div>
                  </td>
                </tr>
              ))}
            </DataTable>

            <div className="md:hidden space-y-4 p-4">
              {rows.map((row) => (
                <div key={row.complaintTypeId}
                  className="p-4 rounded-xl border border-(--color-text-border)/50 bg-white shadow-xs flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground text-sm truncate">{row.complaintType || "—"}</p>
                      {row.complaintDescription ? <p className="mt-1 text-xs per-file-hint line-clamp-2">{row.complaintDescription}</p> : null}
                    </div>
                    {editButton(row)}
                  </div>
                  {statusToggle(row)}
                </div>
              ))}
            </div>

            <TablePagination page={page} totalPages={totalPages} totalItems={totalItems}
              pageSize={PAGE_SIZE} onPageChange={setPage} />
          </>
        )}
      </ContentCard>

      <ComplaintTypeDialog open={dialog.open} productServiceId={productServiceId} record={dialog.record}
        onClose={() => setDialog({ open: false, record: null })} onSaved={handleSaved} />
    </AppLayout>
  );
}
